import * as Result from "@gordonb/result/result";
import { toBearerToken } from "../../util/bearer-token.ts";
import { truncate } from "../../util/truncate.ts";
import { ResultSchemaOf } from "../../models/result.ts";
import { GENERATE_TITLE_API_URL } from "../client-config.ts";
import * as z from "zod/v4";

// --- Shared helper ---

const postGenerate = async (
  url: string,
  // deno-lint-ignore no-explicit-any
  schema: z.ZodType<any>,
  token: string | undefined,
  body: Record<string, unknown>,
  // deno-lint-ignore no-explicit-any
): Promise<any> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: toBearerToken(token),
      },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    return schema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

// --- Title ---

const GenerateTitleResultSchema = ResultSchemaOf(
  z.object({ title: z.string() }),
  z.string(),
);

export type GenerateTitleResult = z.infer<typeof GenerateTitleResultSchema>;

export const generateTitle = (
  token: string | undefined,
  text: string,
): Promise<GenerateTitleResult> =>
  postGenerate(GENERATE_TITLE_API_URL, GenerateTitleResultSchema, token, {
    text: truncate(text, 1024),
  });

// --- Thinking summary ---

const THINKING_SUMMARY_SYSTEM_PROMPT = `Summarize the following AI reasoning trace into a brief label (3-10 words) describing what was done.
Rules:
- Start with a past-tense verb (e.g. "Analyzed", "Searched", "Updated")
- Use sentence case, not Title Case
- Do not use markdown formatting
Good: 'Analyzed climate data and risk factors'
Good: 'Updated memory and fetched library notes'
Bad: 'Climate Data Analysis' (no verb, Title Case)
Bad: 'Memory Update and Library Fetch' (no verb, Title Case)
Return only the label.`;

const GenerateThinkingSummaryResultSchema = ResultSchemaOf(
  z.object({ title: z.string() }),
  z.string(),
);

export type GenerateThinkingSummaryResult = z.infer<
  typeof GenerateThinkingSummaryResultSchema
>;

export const generateThinkingSummary = (
  token: string | undefined,
  text: string,
): Promise<GenerateThinkingSummaryResult> =>
  postGenerate(
    GENERATE_TITLE_API_URL,
    GenerateThinkingSummaryResultSchema,
    token,
    {
      text: truncate(text, 2048),
      system_prompt: THINKING_SUMMARY_SYSTEM_PROMPT,
      max_chars: 100,
    },
  );
